import { Fragment, type ReactNode } from 'react';
import { Icon } from './Icon';

const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ');

/* ---------- SidebarNav ---------- */
export interface NavItem {
  section?: string;
  value?: string;
  label?: string;
  icon?: string;
  badge?: ReactNode;
}

export interface SidebarNavProps {
  items?: NavItem[];
  value?: string;
  onChange?: (value: string) => void;
  theme?: 'dark' | 'light';
}

export function SidebarNav({ items = [], value, onChange, theme = 'dark' }: SidebarNavProps) {
  return (
    <nav className={cx('zen-nav', theme === 'light' && 'zen-nav--light')}>
      {items.map((it, i) =>
        it.section ? (
          <div key={'s' + i} className="zen-nav__section">
            {it.section}
          </div>
        ) : (
          <button
            key={it.value}
            className={cx('zen-navitem', value === it.value && 'zen-navitem--active')}
            onClick={() => it.value && onChange?.(it.value)}
          >
            {it.icon && <Icon name={it.icon} size="17px" />}
            <span>{it.label}</span>
            {it.badge != null && <span className="zen-navitem__badge">{it.badge}</span>}
          </button>
        ),
      )}
    </nav>
  );
}

/* ---------- Tabs ---------- */
type TabItem = string | { value: string; label: string };
export interface TabsProps {
  items?: TabItem[];
  value?: string;
  onChange?: (value: string) => void;
  variant?: 'underline' | 'pill';
}

export function Tabs({ items = [], value, onChange, variant = 'underline' }: TabsProps) {
  return (
    <div className={cx('zen-tabs', variant === 'pill' && 'zen-tabs--pill')} role="tablist">
      {items.map((it) => {
        const id = typeof it === 'string' ? it : it.value;
        const label = typeof it === 'string' ? it : it.label;
        return (
          <button
            key={id}
            role="tab"
            aria-selected={value === id}
            className={cx('zen-tab', value === id && 'zen-tab--active')}
            onClick={() => onChange?.(id)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Pagination ---------- */
export interface PaginationProps {
  page?: number;
  pages?: number;
  onChange?: (page: number) => void;
}

export function Pagination({ page = 1, pages = 1, onChange }: PaginationProps) {
  const go = (p: number) => onChange?.(Math.min(pages, Math.max(1, p)));
  const nums = Array.from({ length: pages }, (_, i) => i + 1).slice(0, 7);
  return (
    <div className="zen-pager">
      <button className="zen-pager__item" disabled={page === 1} onClick={() => go(page - 1)} aria-label="Previous page">
        <Icon name="chevron-left" size="14px" />
      </button>
      {nums.map((n) => (
        <button key={n} className={cx('zen-pager__item', n === page && 'zen-pager__item--active')} onClick={() => go(n)}>
          {n}
        </button>
      ))}
      <button className="zen-pager__item" disabled={page === pages} onClick={() => go(page + 1)} aria-label="Next page">
        <Icon name="chevron-right" size="14px" />
      </button>
    </div>
  );
}

/* ---------- Breadcrumbs ---------- */
type Crumb = string | { label: string; href?: string };
export function Breadcrumbs({ items = [] }: { items?: Crumb[] }) {
  return (
    <nav className="zen-crumbs" aria-label="Breadcrumb">
      {items.map((it, i) => {
        const last = i === items.length - 1;
        const label = typeof it === 'string' ? it : it.label;
        const href = typeof it === 'string' ? undefined : it.href;
        return (
          <Fragment key={i}>
            {i > 0 && <span className="zen-crumbs__sep">/</span>}
            {last ? <span className="zen-crumbs__current">{label}</span> : <a href={href || '#'}>{label}</a>}
          </Fragment>
        );
      })}
    </nav>
  );
}
